import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
} from "../design/dialog";
import {
  ErrorMessage,
  Field,
  FieldGroup,
  Fieldset,
  Label,
} from "../design/fieldset";
import { Input } from "../design/input";
import { useEventTag } from "../contexts/EventTagContext";
import { EventTag } from "../models/EventTag";
import { Textarea } from "../design/textarea";
import { Select } from "../design/select";
import { colors } from "../design/badge";
import { Button } from "../design/button";

interface Context {
  openDialog: (name: string, onCreated?: (eventTag: EventTag) => void) => void;
  closeDialog: () => void;
  isOpen: boolean;
}

interface Errors {
  file?: string;
  name?: string;
  side?: string;
}

const CreateEventTagDialogProviderContext = createContext<Context | undefined>(
  undefined,
);

export const CreateEventTagDialogProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const { createEventTag } = useEventTag();
  const [isOpen, setIsOpen] = useState(false);
  const [errors, setErrors] = useState<Errors>({});

  const [name, setName] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [color, setColor] = useState<string>("red");
  const [isUploading, setIsUploading] = useState(false);
  const [onCreatedCallback, setOnCreatedCallback] = useState<
    ((eventTagId: EventTag) => void) | undefined
  >(undefined);

  const openDialog = (
    name: string,
    onCreated?: (eventTag: EventTag) => void,
  ) => {
    setName(name);
    setOnCreatedCallback(() => onCreated);
    setIsOpen(true);
  };

  const closeDialog = () => {
    setIsOpen(false);
  };

  const validateForm = () => {
    const newErrors: Errors = {};
    if (!name.trim()) newErrors.name = "A name is required.";
    if (!color) newErrors.side = "A color is required.";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsUploading(true);

    try {
      const eventTag = await createEventTag({
        name,
        color,
        description,
      });

      clearForm();
      clearForm();

      if (onCreatedCallback) onCreatedCallback(eventTag);
      closeDialog();
    } catch (error) {
      setErrors({ name: "Failed to create filing. Please try again." });
    }

    setIsUploading(false);
  };

  const clearForm = () => {
    setName("");
    setDescription("");
    setColor("red");
    setErrors({});
    setIsOpen(false);
  };

  return (
    <CreateEventTagDialogProviderContext.Provider
      value={{ openDialog, closeDialog, isOpen }}
    >
      <Dialog open={isOpen} onClose={closeDialog}>
        <DialogTitle>Create Tag</DialogTitle>
        <DialogBody>
          <Fieldset>
            <FieldGroup>
              <Field>
                <Label>Name</Label>
                <Input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Name"
                />
                {errors.name && <ErrorMessage>{errors.name}</ErrorMessage>}
              </Field>

              <Field>
                <Label>Color</Label>
                <Select
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                >
                  {Object.keys(colors).map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </Select>
              </Field>

              <Field>
                <Label>Description</Label>
                <Textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Description"
                />
              </Field>
            </FieldGroup>
          </Fieldset>
        </DialogBody>
        <DialogActions>
          <Button plain onClick={closeDialog}>
            Cancel
          </Button>
          <Button disabled={isUploading} onClick={handleSubmit}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
      {children}
    </CreateEventTagDialogProviderContext.Provider>
  );
};

export const useCreateEventTagDialog = () => {
  const ctx = useContext(CreateEventTagDialogProviderContext);
  if (!ctx)
    throw new Error(
      "useCreateEventTagDialog must be used inside CreateEventTagDialogProvider",
    );
  return ctx;
};
